import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Globe2, ChevronDown, Plus, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const T = {
  fr: { title: 'GPT Africa — langues régionales d\'Afrique', sub: 'Choisis ton pays : chaque pays a son propre sous-groupe de traductions.', pick: 'Choisir un pays', joined: 'Rejoint', join: 'Rejoindre', langs: 'langues' },
  en: { title: 'GPT Africa — African regional languages', sub: 'Pick your country: each country has its own translation sub-group.', pick: 'Choose a country', joined: 'Joined', join: 'Join', langs: 'languages' },
  es: { title: 'GPT Africa — lenguas regionales de África', sub: 'Elige tu país: cada país tiene su propio subgrupo de traducciones.', pick: 'Elegir un país', joined: 'Unido', join: 'Unirse', langs: 'idiomas' },
  ar: { title: 'GPT Africa — اللغات الإقليمية الأفريقية', sub: 'اختر بلدك: لكل بلد مجموعة فرعية خاصة بالترجمات.', pick: 'اختر بلدًا', joined: 'منضم', join: 'انضم', langs: 'لغات' },
  pt: { title: 'GPT Africa — línguas regionais de África', sub: 'Escolha o seu país: cada país tem o seu próprio subgrupo de traduções.', pick: 'Escolher um país', joined: 'Membro', join: 'Entrar', langs: 'línguas' },
  de: { title: 'GPT Africa — afrikanische Regionalsprachen', sub: 'Wähle dein Land: jedes Land hat seine eigene Übersetzungs-Untergruppe.', pick: 'Land wählen', joined: 'Beigetreten', join: 'Beitreten', langs: 'Sprachen' },
  ru: { title: 'GPT Africa — региональные языки Африки', sub: 'Выберите страну: у каждой страны своя подгруппа переводов.', pick: 'Выбрать страну', joined: 'Вы участник', join: 'Вступить', langs: 'языков' },
  zh: { title: 'GPT Africa — 非洲地区语言', sub: '选择你的国家：每个国家都有自己的翻译子群组。', pick: '选择国家', joined: '已加入', join: '加入', langs: '种语言' },
  ja: { title: 'GPT Africa — アフリカの地域言語', sub: '国を選んでください：国ごとに翻訳サブグループがあります。', pick: '国を選ぶ', joined: '参加済み', join: '参加', langs: '言語' },
  hi: { title: 'GPT Africa — अफ़्रीकी क्षेत्रीय भाषाएँ', sub: 'अपना देश चुनें: हर देश का अपना अनुवाद उप-समूह है।', pick: 'देश चुनें', joined: 'शामिल', join: 'शामिल हों', langs: 'भाषाएँ' },
  nl: { title: 'GPT Africa — Afrikaanse regionale talen', sub: 'Kies je land: elk land heeft zijn eigen vertaal-subgroep.', pick: 'Kies een land', joined: 'Lid', join: 'Deelnemen', langs: 'talen' },
  sw: { title: 'GPT Africa — lugha za kikanda za Afrika', sub: 'Chagua nchi yako: kila nchi ina kikundi kidogo chake cha tafsiri.', pick: 'Chagua nchi', joined: 'Umejiunga', join: 'Jiunge', langs: 'lugha' },
  ht: { title: 'GPT Africa — lang rejyonal Afrik yo', sub: 'Chwazi peyi ou: chak peyi gen pwòp sou-gwoup tradiksyon li.', pick: 'Chwazi yon peyi', joined: 'Manm', join: 'Antre', langs: 'lang' },
};
export const africaTexts = (l) => T[l] || T.en;

// Groups list card: one umbrella "GPT Africa" with per-country sub-groups
export const GptAfricaCard = ({ onJoined }) => {
  const { language } = useLanguage();
  const A = africaTexts(language);
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    axios.get('/api/gpt-africa/info', { params: { lang: language } }).then((r) => setInfo(r.data)).catch(() => {});
  }, [language]);

  const join = async (c) => {
    setBusy(c.code);
    try {
      await axios.post('/api/gpt-africa/join', { country: c.code });
      setInfo((i) => ({ ...i, joined_group_ids: [...(i?.joined_group_ids || []), c.group_id] }));
      toast.success(`${c.flag} ${c.name}`);
      onJoined?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } finally { setBusy(''); }
  };

  return (
    <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-emerald-500/10 to-rose-500/10 p-3" data-testid="gpt-africa-card">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 text-left" data-testid="gpt-africa-toggle">
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-500 to-emerald-600 flex items-center justify-center shrink-0"><Globe2 className="w-5 h-5 text-white" /></div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white leading-tight">{A.title}</p>
          <p className="text-[11px] text-gray-300 mt-0.5">{A.sub}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-300 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-3 max-h-64 overflow-y-auto space-y-1" data-testid="gpt-africa-countries">
          <p className="text-[10px] uppercase tracking-wider text-amber-200/80 font-bold px-1">{A.pick}</p>
          {(info?.countries || []).map((c) => {
            const joined = (info?.joined_group_ids || []).includes(c.group_id);
            return (
              <div key={c.code} className="flex items-center gap-2 px-2 py-1.5 rounded-xl bg-black/30" data-testid={`gpt-africa-country-${c.code}`}>
                <span className="text-lg">{c.flag}</span>
                <span className="flex-1 text-sm text-white truncate">{c.name} <span className="text-[10px] text-gray-400">· {c.languages} {A.langs}</span></span>
                <button onClick={() => !joined && join(c)} disabled={joined || busy === c.code}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-semibold flex items-center gap-1 ${joined ? 'bg-emerald-500/20 text-emerald-200' : 'bg-amber-500 text-black hover:bg-amber-400'}`}
                  data-testid={`gpt-africa-join-${c.code}`}>
                  {joined ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}{joined ? A.joined : A.join}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GptAfricaCard;
